/**
 * 15 §3.4 results screen (A04-19). Canvas overlay over the dimmed arena;
 * all rows pen-revealed with a 90 ms stagger (`ui_row_in`), accolades from
 * the server (`result.accolades`, 04 §10 ruling) rendered as tags.
 */
import { VIRTUAL_H, VIRTUAL_W } from '../../../sim/constants.js'
import type { Team } from '../../../sim/types.js'
import type { MatchResultMsg } from '../../../shared/protocol.js'
import { glyphForPowerUp } from '../glyphs.js'
import { bracket, hudFont, inkAlpha, monoFont, row, scoreCell, tabularText, tag, teamAccent, teamGlyph, teamLetter, text, type RowCol } from './components.js'

export const RESULTS_ROW_STAGGER_MS = 90
/** 15 §3.4: blueprint paper tone under the table (0.92 alpha, no blur) so cells never collide with floor annotations. */
export const PAPER_GROUND = 'rgba(15,20,24,0.92)'
export const RESULTS_ROW_REVEAL_MS = 200
export type ResultsButton = 'hub' | 'rematch' | 'spectate'

export interface ResultsShowOpts { localId: string | null; localTeam: Team | null; spectator: boolean; now: number; participants: number }
export interface ResultsDrawOpts { reducedMotion: boolean; rematchVotes: number; autoReturnLeftMs: number; rematchPressed?: boolean }

export type ResultRow = MatchResultMsg['players'][number]

/** Accolade for a player: server map first, legacy per-player field second. */
export function accoladeFor(result: MatchResultMsg, id: string): string | null {
  const fromMap = result.accolades?.[id]
  if (fromMap !== undefined) return fromMap
  return result.players.find((p) => p.id === id)?.accolade ?? null
}

/** Rows sorted by team, then eliminations desc, then name. */
export function sortedRows(result: MatchResultMsg): ResultRow[] {
  return [...result.players].sort((a, b) => a.team - b.team || b.stats.eliminations - a.stats.eliminations || a.name.localeCompare(b.name))
}

/** Headline from the viewer's perspective. */
export function headline(result: MatchResultMsg, localTeam: Team | null, spectator: boolean): string {
  if (result.winner === null) return 'MATCH ABANDONED'
  if (!spectator && localTeam !== null) return result.winner === localTeam ? 'VICTORY' : 'DEFEAT'
  return `TEAM ${teamLetter(result.winner)} WINS`
}

export function tiebreakLine(result: MatchResultMsg): string | null {
  const last = [...result.rounds].reverse().find((r) => !r.void)
  if (!last) return null
  const n = result.rounds.filter((r) => !r.void).indexOf(last) + 1
  switch (last.reason) {
    case 'coin': return `ROUND ${n} — TIEBREAK: COIN`
    case 'ladder_round_elims': return `ROUND ${n} — TIEBREAK: ELIMINATIONS`
    case 'ladder_perfects': return `ROUND ${n} — TIEBREAK: PERFECT DEFLECTS`
    case 'ladder_match_elims': return `ROUND ${n} — TIEBREAK: MATCH ELIMINATIONS`
    case 'forfeit': return `ROUND ${n} — FORFEIT`
    default: return null
  }
}

/** Pen-reveal progress of row `i` at time `t` since show (0..1). */
export function rowProgress(i: number, sinceMs: number, reducedMotion: boolean): number {
  if (reducedMotion) return 1
  return Math.max(0, Math.min(1, (sinceMs - i * RESULTS_ROW_STAGGER_MS) / RESULTS_ROW_REVEAL_MS))
}

/** 15 §3.4 stat columns (row-local x, px). S10: JUMP appended — the stat columns tightened from 60 to 55 px pitch so the
 *  accolade tag still ends inside the 880 px row at 1280 (panel 960 − 2 × 40 inset). */
export const RESULTS_COLS = { name: 40, glyph: 230, elim: 300, bank: 355, perf: 410, catch: 465, steal: 520, dash: 575, jump: 630, shield: 685, accolade: 760 }
const COLS = RESULTS_COLS
export const RESULTS_HEADERS: readonly [string, number][] = [['ELIM', COLS.elim], ['BANK', COLS.bank], ['PERF', COLS.perf], ['CATCH', COLS.catch], ['STEAL', COLS.steal], ['DASH', COLS.dash], ['JUMP', COLS.jump], ['SHIELD s', COLS.shield], ['ACCOLADE', COLS.accolade]]
const HEADERS = RESULTS_HEADERS

export class ResultsScreen {
  result: MatchResultMsg | null = null
  private shownAt = 0
  private opts: ResultsShowOpts | null = null
  private revealed = new Set<number>()
  private accoladeSeen = new Set<number>()
  focus: ResultsButton = 'hub'
  /** Another live match's room id (10 §3 "spectate next"), refreshed from the kiosk feed each frame. */
  private liveMatch: string | null = null
  private buttons: { id: ResultsButton; x: number; y: number; w: number; h: number }[] = []
  onRowRevealed: (i: number) => void = () => {}
  onAccolade: () => void = () => {}

  show(result: MatchResultMsg, opts: ResultsShowOpts): void {
    this.result = result; this.opts = opts; this.shownAt = opts.now
    this.revealed.clear(); this.accoladeSeen.clear(); this.focus = 'hub'
  }
  hide(): void { this.result = null; this.opts = null; this.buttons = []; this.liveMatch = null }
  setLiveMatch(id: string | null): void { this.liveMatch = id; if (!this.available().includes(this.focus)) this.focus = 'hub' }
  /** Buttons offered right now: RETURN TO HUB, REMATCH, and SPECTATE NEXT while another match is live. */
  available(): ResultsButton[] { return this.liveMatch ? ['hub', 'rematch', 'spectate'] : ['hub', 'rematch'] }
  get visible(): boolean { return this.result !== null }

  /** Row layout: header rows (title/strip/rule) then team/player rows. */
  /** Outcome headline as drawn (VICTORY / DEFEAT / TEAM X WINS) — the results screen supersedes the match call-out (main.ts clears it), so this is where the outcome is announced. */
  title(): string | null { return this.result && this.opts ? headline(this.result, this.opts.localTeam, this.opts.spectator) : null }

  lines(): ({ kind: 'team'; team: Team } | { kind: 'player'; p: ResultRow })[] {
    if (!this.result) return []
    const out: ({ kind: 'team'; team: Team } | { kind: 'player'; p: ResultRow })[] = []
    let lastTeam: Team | null = null
    for (const p of sortedRows(this.result)) {
      if (p.team !== lastTeam) { out.push({ kind: 'team', team: p.team }); lastTeam = p.team }
      out.push({ kind: 'player', p })
    }
    return out
  }

  hitTest(x: number, y: number): ResultsButton | null {
    for (const b of this.buttons) if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return b.id
    return null
  }
  /** Keyboard: Tab/arrows cycle focus; Enter returns the activated button. */
  key(code: string): ResultsButton | null {
    if (!this.opts || this.opts.spectator) return null
    if (code === 'Tab' || code === 'ArrowLeft' || code === 'ArrowRight' || code === 'ArrowUp' || code === 'ArrowDown') { const a = this.available(); this.focus = a[(a.indexOf(this.focus) + 1) % a.length]; return null }
    if (code === 'Enter' || code === 'Space') return this.focus
    return null
  }

  draw(ctx: CanvasRenderingContext2D, now: number, d: ResultsDrawOpts): void {
    const r = this.result, o = this.opts
    if (!r || !o) return
    const since = now - this.shownAt
    ctx.save()
    ctx.fillStyle = 'rgba(11,13,16,0.4)'; ctx.fillRect(0, 0, VIRTUAL_W, VIRTUAL_H)
    const px = 160, py = 60, pw = VIRTUAL_W - 320, ph = VIRTUAL_H - 120
    ctx.fillStyle = PAPER_GROUND; ctx.fillRect(px + 8, py + 8, pw - 16, ph - 16)
    bracket(ctx, px, py, pw, ph, { corners: ['tl', 'tr', 'bl', 'br'], ticks: 3, weight: 2, progress: d.reducedMotion ? 1 : Math.min(1, since / 120) })
    let line = 0
    const prog = (i: number) => { const p = rowProgress(i, since, d.reducedMotion); if (p >= 1 && !this.revealed.has(i)) { this.revealed.add(i); this.onRowRevealed(i) } return p }
    // Headline + score.
    const p0 = prog(line++)
    const head = headline(r, o.localTeam, o.spectator)
    ctx.save(); ctx.beginPath(); ctx.rect(px, py, pw * p0, 120); ctx.clip()
    text(ctx, head, px + 40, py + 80, { font: hudFont(64, 700), ink: r.winner !== null ? teamAccent(r.winner) : undefined })
    if (r.winner !== null) { ctx.save(); ctx.translate(px + 40 + ctx.measureText(head).width, 0); ctx.restore() }
    tabularText(ctx, `${r.score[0]} – ${r.score[1]}`, px + pw - 40, py + 80, 36, { align: 'right' })
    ctx.restore()
    // Round strip (roundNumber order incl. VOID).
    const p1 = prog(line++)
    const strip = [...r.rounds].sort((a, b) => a.roundNumber - b.roundNumber)
    ctx.save(); ctx.beginPath(); ctx.rect(px, py + 100, pw * p1, 40); ctx.clip()
    text(ctx, 'ROUND STRIP', px + 40, py + 124, { font: hudFont(14, 700), alpha: 0.7 })
    strip.forEach((rr, i) => scoreCell(ctx, rr.void ? 'void' : 'won', rr.winner, px + 160 + i * 28, py + 119, 12))
    ctx.restore()
    // Rule + header.
    const p2 = prog(line++)
    ctx.beginPath(); ctx.moveTo(px + 40, py + 150); ctx.lineTo(px + 40 + (pw - 80) * p2, py + 150); ctx.strokeStyle = inkAlpha(0.4); ctx.lineWidth = 1; ctx.stroke()
    row(ctx, HEADERS.map(([t, x]) => ({ text: t, x, font: hudFont(12, 700), align: 'center' as CanvasTextAlign, ink: inkAlpha(0.6) })), px + 40, py + 172, { progress: p2, width: pw })
    // Team / player rows.
    let y = py + 200
    for (const ln of this.lines()) {
      const i = line++
      const p = prog(i)
      if (ln.kind === 'team') {
        row(ctx, [{ text: `TEAM ${teamLetter(ln.team)}`, x: 0, font: hudFont(18, 700), ink: teamAccent(ln.team) }, { text: '', x: 80, glyph: teamGlyph(ln.team), ink: teamAccent(ln.team) }], px + 40, y, { progress: p, width: pw, size: 18 })
        y += 28
        continue
      }
      const pl = ln.p
      const acc = accoladeFor(r, pl.id)
      const cols: RowCol[] = [
        { text: pl.name, x: COLS.name, font: hudFont(16) },
        { text: '', x: COLS.glyph, glyph: glyphForPowerUp(pl.powerUps[0]) },
        { text: String(pl.stats.eliminations), x: COLS.elim, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.bankEliminations), x: COLS.bank, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.perfectDeflects), x: COLS.perf, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.catches), x: COLS.catch, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.steals), x: COLS.steal, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.dashes), x: COLS.dash, align: 'center', font: monoFont(14) },
        { text: String(pl.stats.jumps), x: COLS.jump, align: 'center', font: monoFont(14) },
        { text: (pl.stats.shieldTicks / 60).toFixed(1), x: COLS.shield, align: 'center', font: monoFont(14) },
        acc ? { text: acc.toUpperCase(), x: COLS.accolade, tag: 'neutral' } : { text: '—', x: COLS.accolade, font: hudFont(14), ink: inkAlpha(0.5) },
      ]
      if (pl.bot) cols.push({ text: 'BOT', x: COLS.name + 150, tag: 'neutral' })
      row(ctx, cols, px + 40, y, { progress: p, width: pw })
      if (acc && p >= 1 && !this.accoladeSeen.has(i)) { this.accoladeSeen.add(i); this.onAccolade() }
      y += 26
    }
    // Tiebreak line.
    const tb = tiebreakLine(r)
    const pT = prog(line++)
    ctx.beginPath(); ctx.moveTo(px + 40, y + 4); ctx.lineTo(px + 40 + (pw - 80) * pT, y + 4); ctx.strokeStyle = inkAlpha(0.4); ctx.lineWidth = 1; ctx.stroke()
    if (tb) row(ctx, [{ text: tb, x: 0, font: hudFont(14, 700), ink: inkAlpha(0.8) }], px + 40, y + 26, { progress: pT, width: pw })
    // Buttons.
    const by = py + ph - 40
    this.buttons = []
    const pB = prog(line++)
    if (pB > 0) {
      if (o.spectator) text(ctx, '[ESC] LEAVE', px + 40, by + 6, { font: hudFont(16, 700) })
      else {
        const defs: { id: ResultsButton; label: string }[] = [{ id: 'hub', label: 'RETURN TO HUB' }, { id: 'rematch', label: `REMATCH ${d.rematchVotes}/${o.participants}` }]
        if (this.liveMatch) defs.push({ id: 'spectate', label: 'SPECTATE NEXT' })
        let bx = px + 40
        for (const b of defs) {
          ctx.font = hudFont(16, 700)
          const w = ctx.measureText(b.label).width + 28, h = 30
          this.buttons.push({ id: b.id, x: bx, y: by - 20, w, h })
          const focused = this.focus === b.id
          const pressed = b.id === 'rematch' && d.rematchPressed
          bracket(ctx, bx, by - 20, w, h, { corners: focused ? ['tl', 'tr', 'bl', 'br'] : ['tl', 'br'], weight: focused ? 2.5 : 1.5, progress: pB })
          text(ctx, `[ ${b.label} ]`, bx + 14, by + 1, { font: hudFont(16, 700), ink: pressed ? inkAlpha(1) : undefined, alpha: pressed ? 1 : 0.9 })
          bx += w + 24
        }
      }
      const s = Math.max(0, Math.ceil(d.autoReturnLeftMs / 1000))
      text(ctx, `auto-return 0:${String(s).padStart(2, '0')}`, px + pw - 40, by + 6, { font: monoFont(13), align: 'right', alpha: 0.7 })
    }
    ctx.restore()
  }
}

export { tag }
